import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { UseMutationResult, UseQueryResult } from '@tanstack/react-query';
import {
  getOrCreatePromotionLink,
  listPromotionAnalytics,
  listPromotionTargets,
  recordPromotionEvent,
} from '../services/promotion-kit.service';
import type { SupabaseClient } from '../supabase/client';
import type {
  GetOrCreatePromotionLinkInput,
  PromotionAnalyticsSummary,
  PromotionLinkWithUrl,
  PromotionTargetType,
  PromotionTargetsPage,
  RecordPromotionEventInput,
} from '../types/promotion-kit';

export const promotionKitKeys = {
  all: ['promotion-kit'] as const,
  targets: (storeId: string, targetType: string | null, search: string | null, offset: number) =>
    ['promotion-kit', 'targets', storeId, targetType ?? 'all', search ?? '', offset] as const,
  link: (storeId: string, targetType: string, targetId: string | null) =>
    ['promotion-kit', 'link', storeId, targetType, targetId ?? 'none'] as const,
  analytics: (storeId: string, since: string | null) =>
    ['promotion-kit', 'analytics', storeId, since ?? 'all'] as const,
};

export function usePromotionTargets(args: {
  readonly client: SupabaseClient;
  readonly storeId: string | null;
  readonly supabaseUrl: string;
  readonly targetType?: PromotionTargetType | null;
  readonly search?: string | null;
  readonly limit?: number;
  readonly offset?: number;
}): UseQueryResult<PromotionTargetsPage> {
  const search = args.search?.trim() ?? null;
  const offset = args.offset ?? 0;
  return useQuery({
    queryKey: args.storeId
      ? promotionKitKeys.targets(args.storeId, args.targetType ?? null, search, offset)
      : promotionKitKeys.all,
    enabled: !!args.storeId,
    queryFn: () =>
      listPromotionTargets(args.client, {
        storeId: args.storeId ?? '',
        targetType: args.targetType ?? null,
        search,
        limit: args.limit,
        offset,
        supabaseUrl: args.supabaseUrl,
      }),
    placeholderData: keepPreviousData,
    staleTime: 30_000,
  });
}

export function useGetOrCreatePromotionLink(
  client: SupabaseClient,
): UseMutationResult<PromotionLinkWithUrl, unknown, GetOrCreatePromotionLinkInput> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input) => getOrCreatePromotionLink(client, input),
    onSuccess: (link) => {
      void queryClient.invalidateQueries({
        queryKey: promotionKitKeys.analytics(link.store_id, null),
      });
    },
  });
}

export function usePromotionAnalytics(args: {
  readonly client: SupabaseClient;
  readonly storeId: string | null;
  readonly since?: string | null;
  readonly enabled?: boolean;
}): UseQueryResult<PromotionAnalyticsSummary> {
  return useQuery({
    queryKey: args.storeId
      ? promotionKitKeys.analytics(args.storeId, args.since ?? null)
      : promotionKitKeys.all,
    enabled: !!args.storeId && (args.enabled ?? true),
    queryFn: () =>
      listPromotionAnalytics(args.client, {
        storeId: args.storeId ?? '',
        since: args.since ?? null,
      }),
    staleTime: 30_000,
  });
}

export function useRecordPromotionEvent(
  client: SupabaseClient,
): UseMutationResult<void, unknown, RecordPromotionEventInput> {
  return useMutation({ mutationFn: (input) => recordPromotionEvent(client, input) });
}
